import React, { useEffect, useMemo, useState } from "react";

function isoToDisplay(iso: string) {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "";
  const [, y, mm, dd] = m;
  return `${dd}/${mm}/${y}`;
}

function isValidYMD(y: number, m: number, d: number) {
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return false;
  if (y < 1900 || y > 2100) return false;
  if (m < 1 || m > 12) return false;
  if (d < 1 || d > 31) return false;
  const dt = new Date(y, m - 1, d);
  return (
    dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d
  );
}

function displayToISO(value: string) {
  const v = value.trim();
  if (!v) return null;

  // Accept dd/MM/yyyy or dd-MM-yyyy
  const dmY = v.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (dmY) {
    const d = Number(dmY[1]);
    const m = Number(dmY[2]);
    const y = Number(dmY[3]);
    if (!isValidYMD(y, m, d)) return null;
    const mm = String(m).padStart(2, "0");
    const dd = String(d).padStart(2, "0");
    return `${y}-${mm}-${dd}`;
  }

  // Also accept ISO yyyy-MM-dd
  const iso = v.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (iso) {
    const y = Number(iso[1]);
    const m = Number(iso[2]);
    const d = Number(iso[3]);
    if (!isValidYMD(y, m, d)) return null;
    const mm = String(m).padStart(2, "0");
    const dd = String(d).padStart(2, "0");
    return `${y}-${mm}-${dd}`;
  }

  return null;
}

type Props = {
  valueISO: string;
  onChangeISO: (nextISO: string) => void;
  fallbackISO?: string;
  className?: string;
  placeholder?: string;
  title?: string;
};

const DateInputDDMMYYYY: React.FC<Props> = ({
  valueISO,
  onChangeISO,
  fallbackISO,
  className,
  placeholder = "dd/MM/yyyy",
  title,
}) => {
  const initialDisplay = useMemo(() => isoToDisplay(valueISO), [valueISO]);
  const [displayValue, setDisplayValue] = useState<string>(initialDisplay);

  useEffect(() => {
    setDisplayValue(isoToDisplay(valueISO));
  }, [valueISO]);

  return (
    <div className="relative inline-block">
      <input
        type="text"
        inputMode="numeric"
        value={displayValue}
        placeholder={placeholder}
        title={title}
        onChange={(e) => {
          const next = e.target.value;
          setDisplayValue(next);

          if (!next.trim()) {
            if (fallbackISO) {
              onChangeISO(fallbackISO);
            }
            return;
          }

          const parsed = displayToISO(next);
          if (parsed) {
            onChangeISO(parsed);
          }
        }}
        onBlur={() => {
          const trimmed = displayValue.trim();
          if (!trimmed) {
            if (fallbackISO) {
              onChangeISO(fallbackISO);
              setDisplayValue(isoToDisplay(fallbackISO));
            } else {
              setDisplayValue(isoToDisplay(valueISO));
            }
            return;
          }

          const parsed = displayToISO(trimmed);
          if (parsed) {
            if (parsed !== valueISO) onChangeISO(parsed);
            setDisplayValue(isoToDisplay(parsed));
          } else {
            setDisplayValue(isoToDisplay(valueISO));
          }
        }}
        className={`${className ?? ""} pr-10`}
      />

      {/* Native date picker trigger (icon area) */}
      <div
        className="absolute right-0 top-0 h-full w-9 flex items-center justify-center"
        aria-hidden="true"
      >
        <svg
          className="h-4 w-4 text-gray-500 dark:text-gray-300 pointer-events-none"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      </div>

      {/* Invisible but clickable date input over the icon area */}
      <input
        type="date"
        value={valueISO}
        onChange={(e) => {
          const nextISO = e.target.value;
          if (nextISO) onChangeISO(nextISO);
          else if (fallbackISO) onChangeISO(fallbackISO);
        }}
        title="Chọn ngày"
        className="absolute right-0 top-0 h-full w-9 opacity-0 cursor-pointer"
      />
    </div>
  );
};

export default DateInputDDMMYYYY;

